<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        CardLayout,
        NotificationModal,
        PageLoading,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";

    import {
        getPrivateInsuranceFundCoverTypes,
        getPrivateInsuranceFundDefaultCoverType,
        getText,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import { addIcon } from "../../../Utils/utilConstants.svelte";
    import { PrivateInsuranceFundCoverType } from "../../../Utils/utilEnums.svelte";

    export let accountId = "";

    const columns = [
        {
            key: "fundId",
            title: "Fund ID",
            headerClass: "text-left pl-4 text-[14px] w-1/8",
            class: "text-left pl-4 text-[14px] cursor-default w-1/8",
            value: (v) => getText(v.insuranceId),
        },
        {
            key: "privateInsuranceFundName",
            title: "Insurance Fund Name",
            headerClass: "text-left pl-4 text-[14px] w-1/2",
            class: "text-left pl-4 text-[14px] cursor-default w-1/2",
            value: (v) => getText(v.insuranceName),
        },
        {
            key: "defaultCoverType",
            title: "Default Cover Type",
            headerClass: "text-left pl-4 text-[14px] w-1/4",
            class: "text-left pl-4 text-[14px] cursor-default w-1/4",
            value: (v) => _getColDefaultCoverTypeDisplayName(v),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-4 text-[14px] w-[2%]",
            class: "text-left px-4 text-[14px] cursor-auto",
            value: (v) => _renderActionIcons(),
        },
    ];

    $: btnOptions = [
        {
            iconName: "addIcon",
            iFont: addIcon,
            iconFunction: () => _openAddDefaultInsuranceCoverModal(),
            btnText: "Add",
            isDisabled:
                defaultInsuranceCovers.length ==
                PrivateInsuranceFundCoverType.length,
            hasTooltip:
                defaultInsuranceCovers.length ==
                PrivateInsuranceFundCoverType.length,
            tooltip:
                "Currently no more supported default insurance covers available",
        },
    ];

    let isGettingDefaultInsuranceCovers = false,
        defaultInsuranceCovers = [],
        availableInsuranceCovers = [];

    let isAddDefaultInsuranceCoverModalOpen = false,
        isEditDefaultInsuranceCoverModalOpen = false,
        isModifyingDefaultInsuranceCoverType = false;

    let defaultInsuranceCoverObj = {};

    onMount(async () => await loadDefaultInsuranceCovers());

    const loadDefaultInsuranceCovers = async () => {
        isGettingDefaultInsuranceCovers = true;

        await apiMethods.getMethod
            .getAccountInsuranceDefaultCover(accountId)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load account default insurance covers, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    availableInsuranceCovers = [];
                    defaultInsuranceCovers = [...res];
                    PrivateInsuranceFundCoverType.map((type) => {
                        if (
                            !defaultInsuranceCovers.some(
                                (cover) => cover.insuranceId == type.id,
                            )
                        )
                            availableInsuranceCovers.push(type);
                    });
                }
            });

        isGettingDefaultInsuranceCovers = false;
    };

    const createDefaultInsuranceCover = async () => {
        defaultInsuranceCoverObj.accountId = accountId;

        isModifyingDefaultInsuranceCoverType = true;

        await apiMethods.postMethod
            .createAccountInsuranceDefaultCover(defaultInsuranceCoverObj)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to add new default insurance cover for this account, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await loadDefaultInsuranceCovers();
            })
            .catch((err) => showError(err));

        isModifyingDefaultInsuranceCoverType = false;

        _closeModifyDefaultInsuranceCoverModal();
    };

    const updateDefaultInsuranceCover = async () => {
        defaultInsuranceCoverObj.accountId = accountId;

        isModifyingDefaultInsuranceCoverType = true;

        await apiMethods.putMethod
            .updateAccountInsuranceDefaultCover(defaultInsuranceCoverObj)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update default insurance cover, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await loadDefaultInsuranceCovers();
            })
            .catch((err) => showError(err));

        isModifyingDefaultInsuranceCoverType = false;

        _closeModifyDefaultInsuranceCoverModal();
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "actions") {
            if (className.includes("editIcon"))
                _openEditDefaultInsuranceCoverModal(rowData);
        }
    };

    const handleInsuranceFundChange = () =>
        (defaultInsuranceCoverObj.insurance =
            getPrivateInsuranceFundDefaultCoverType(
                defaultInsuranceCoverObj.insuranceId,
            ));

    // HELPER FUNCTIONS
    const _getColDefaultCoverTypeDisplayName = (data) => {
        const { insuranceId, insurance } = data;

        if(insurance){
             return getPrivateInsuranceFundCoverTypes(insuranceId).find(
                (fund) => fund.value.toUpperCase() == insurance.toUpperCase(),
            ).displayName;
        }
        return "";
       
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Default Insurance Fund Covers">
                        <i class="editIcon fa-regular fa-pen-to-square"></i>
                    </div>
                </div>`;
    };

    const _openAddDefaultInsuranceCoverModal = () => {
        isAddDefaultInsuranceCoverModalOpen = true;

        defaultInsuranceCoverObj.insuranceId = availableInsuranceCovers[0].id;

        handleInsuranceFundChange();
    };

    const _openEditDefaultInsuranceCoverModal = (data) => {
        defaultInsuranceCoverObj = {};
        isEditDefaultInsuranceCoverModalOpen = true;
        const { id, insurance, insuranceId, insuranceName } = data;
        defaultInsuranceCoverObj.id = id;
        defaultInsuranceCoverObj.insurance = insurance;
        defaultInsuranceCoverObj.insuranceId = insuranceId;
        defaultInsuranceCoverObj.insuranceName = insuranceName;
    };

    const _closeModifyDefaultInsuranceCoverModal = () => {
        isAddDefaultInsuranceCoverModalOpen = false;
        isEditDefaultInsuranceCoverModalOpen = false;
        defaultInsuranceCoverObj = {};
    };
</script>

<CardLayout title="Default Insurance Funds Covers" {btnOptions}>
    {#if isGettingDefaultInsuranceCovers}
        <PageLoading />
    {:else if defaultInsuranceCovers.length}
        <div class="flex flex-1 overflow-y-auto">
            <SvelteTable
                {columns}
                rows={defaultInsuranceCovers}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>No Default Insurance Cover been set yet.</p>
            </div>
        </div>
    {/if}
</CardLayout>

<NotificationModal
    title="Add Default Insurance Funds Cover"
    isModalOpen={isAddDefaultInsuranceCoverModalOpen}
    cancelText="Cancel"
    closeModal={_closeModifyDefaultInsuranceCoverModal}
    closeModalFunction={_closeModifyDefaultInsuranceCoverModal}
    submitText={isModifyingDefaultInsuranceCoverType ? "Adding" : "Add"}
    confirmAction={createDefaultInsuranceCover}
    isConfirming={isModifyingDefaultInsuranceCoverType}
>
    <div class="flex flex-col gap-y-4 p-4">
        <div class="flex w-full gap-x-2 items-center">
            <p class="w-1/3">Insurance Fund:</p>
            <select
                class="select select-bordered select-sm flex flex-1"
                bind:value={defaultInsuranceCoverObj.insuranceId}
                on:change={handleInsuranceFundChange}
            >
                {#each availableInsuranceCovers as { id, fundName }}
                    <option value={id}>{fundName}</option>
                {/each}
            </select>
        </div>

        <div class="flex w-full gap-x-2 items-center">
            <p class="w-1/3">Cover Type:</p>
            <select
                class="select select-bordered select-sm flex flex-1"
                bind:value={defaultInsuranceCoverObj.insurance}
            >
                {#each getPrivateInsuranceFundCoverTypes(defaultInsuranceCoverObj.insuranceId) as { value, displayName, isDefault }, index}
                    <option
                        {value}
                        selected={defaultInsuranceCoverObj.insurance == value ||
                            (!defaultInsuranceCoverObj.insurance &&
                                (isDefault || index == 0))}
                    >
                        {displayName}
                    </option>
                {/each}
            </select>
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="Edit Default Insurance Cover"
    isModalOpen={isEditDefaultInsuranceCoverModalOpen}
    cancelText="Cancel"
    closeModal={_closeModifyDefaultInsuranceCoverModal}
    closeModalFunction={_closeModifyDefaultInsuranceCoverModal}
    submitText={isModifyingDefaultInsuranceCoverType ? "Updating" : "Update"}
    confirmAction={updateDefaultInsuranceCover}
    isConfirming={isModifyingDefaultInsuranceCoverType}
>
    <div class="flex flex-col gap-y-4 p-4">
        <div class="flex w-full gap-x-2 items-center">
            <p class="w-1/3">Insurance Fund:</p>
            <p class="w-2/3">
                {defaultInsuranceCoverObj.insuranceName}
            </p>
        </div>

        <div class="flex w-full gap-x-2 items-center">
            <p class="w-1/3">Cover Type:</p>
            <select
                class="select select-bordered select-sm flex flex-1"
                bind:value={defaultInsuranceCoverObj.insurance}
            >
                {#each getPrivateInsuranceFundCoverTypes(defaultInsuranceCoverObj.insuranceId) as { value, displayName, isDefault }, index}
                    <option
                        {value}
                        selected={defaultInsuranceCoverObj.insurance == value ||
                            (!defaultInsuranceCoverObj.insurance &&
                                (isDefault || index == 0))}
                    >
                        {displayName}
                    </option>
                {/each}
            </select>
        </div>
    </div>
</NotificationModal>
